'use client';

/**
 * ui-future Shell — the v4 nav state machine.
 * State: { deck } for card decks · { surface } for opened tools/spaces.
 * Back law: history stack; BACK/Esc closes overlays first, never reopens one.
 * Dive: pinched card scales ×34 under a light flash; next view arrives.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCarousel, cardStyle } from '@/future/physics/useCarousel';
import { DECKS, DECK_TITLE, type Card } from '@/future/shell/decks';
import { useDeckStats, type CardStat } from '@/future/shell/cardStats';
import { Glass, mono, display } from '@/future/primitives';
import { SurfaceHost } from '@/future/surfaces/SurfaceHost';

type View = { deck: string } | { surface: string };

export function Shell() {
  const [view, setView] = useState<View>({ deck: 'menu' });

  // deep-link law (v4 spec): ?deck= / ?surface= land you in the right place.
  // Applied post-mount — reading window.location during render breaks SSR hydration.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const surface = sp.get('surface');
    if (surface) { setView({ surface }); return; }
    const deck = sp.get('deck');
    if (deck && DECKS[deck]) setView({ deck });
  }, []);
  const [diving, setDiving] = useState<number | null>(null);
  const hist = useRef<View[]>([]);

  /* fly-through screen change — v6 §4: out scale→2.5 (back: .5) fade .42s
     cubic-bezier(.6,0,.85,.4); in from .5 (back: 2.5) → 1, .6s
     cubic-bezier(.2,1.1,.3,1). NO flash — the dive flash stays deck-only. */
  const [fly, setFly] = useState<{ phase: 'out' | 'pre' | 'in'; dir: 'fwd' | 'back' } | null>(null);
  const flyTo = useCallback((mut: () => void, dir: 'fwd' | 'back') => {
    setFly({ phase: 'out', dir });
    setTimeout(() => {
      mut();
      setFly({ phase: 'pre', dir }); // jump-cut: new screen parked at its entry scale, no transition
      requestAnimationFrame(() => requestAnimationFrame(() => {
        setFly({ phase: 'in', dir });
        setTimeout(() => setFly(null), 600);
      }));
    }, 420);
  }, []);

  const navigate = useCallback((next: View, opts?: { dive?: boolean }) => {
    hist.current.push(viewRef.current);
    if (opts?.dive) { setView(next); return; } // deck dives carry their own motion
    flyTo(() => setView(next), 'fwd');
  }, [flyTo]);
  const viewRef = useRef(view);
  viewRef.current = view;

  const back = useCallback(() => {
    const prev = hist.current.pop();
    if (prev) flyTo(() => setView(prev), 'back');
  }, [flyTo]);

  /* PAGE AWARENESS (Boss 2026-07-11 'you should understand what page I am on, what I'm looking
     at'): publish the current screen so every voice turn is grounded in what Boss actually sees. */
  useEffect(() => {
    const label = 'deck' in view
      ? `the "${DECK_TITLE[view.deck] || view.deck}" deck (a grid of cards)`
      : `the "${view.surface}" screen`;
    (window as any).__beastView = label;
    (window as any).__beastViewRaw = view;
    // Boss 2026-07-11: screenshot after every action — capture once the fly-through settles.
    const t = setTimeout(() => { (window as any).beast?.invoke?.('view:capture').catch(() => {}); }, 750);
    return () => clearTimeout(t);
  }, [view]);

  /* VOICE CONTROL (Boss 2026-07-11 'control the beast with my voice — go back, open cobbler,
     next project'): the orb classifies a spoken command and fires beast:voice-command; the Shell
     turns it into real navigation. Everything you can click, you can say. */
  useEffect(() => {
    const cards = Object.entries(DECKS).flatMap(([deck, list]) => list.map(c => ({ ...c, deck })));
    const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
    /* PROJECT-SCOPED RESOLUTION (Boss 2026-07-13 'you opened WTF's Improve, why?'): per-project
       tool cards share ids across projects (every project has an 'improve', 'ads', 'websites'...),
       so a bare id used to grab the FIRST match regardless of context. Derive the project Boss is
       currently in from the live view (surface 'improve:olga' -> 'olga'; deck 'tools:olga' ->
       'olga') and give cards from THAT project's tool deck a decisive boost, so 'open improve'
       opens the improve for the project you're already looking at. */
    const currentProject = (): string | null => {
      const v: any = viewRef.current;
      const raw = v && ('surface' in v ? String(v.surface) : 'deck' in v ? String(v.deck) : '');
      if (!raw) return null;
      if (raw.includes(':')) return raw.split(':').pop() || null;      // improve:olga -> olga
      const m = raw.match(/^tools(?:-person)?:(.+)$/);                 // tools:olga -> olga
      return m ? m[1] : null;
    };
    const resolve = (arg: string) => {
      const q = norm(arg); if (!q) return null;
      const proj = currentProject();
      const inProject = (c: any) => proj && (c.deck === `tools:${proj}` || c.deck === `tools-person:${proj}`);
      let best: any = null, score = 0;
      for (const c of cards) {
        const hay = norm(`${c.id} ${c.title} ${(c as any).sub || ''}`);
        let s = 0;
        if (norm(c.id) === q || norm(c.title) === q) s = 100;
        else if (hay.includes(q)) s = 60;
        else { const qt = q.split(' ').filter(w => w.length > 2); s = qt.filter(w => hay.includes(w)).length * 12; }
        if (s > 0 && inProject(c)) s += 50; // current project wins ties for shared tool ids
        if (s > score) { score = s; best = c; }
      }
      return score >= 12 ? best : null;
    };
    const openCard = (c: any) => { const o = c.opens || {}; if (o.deck) navigate({ deck: o.deck }); else if (o.surface) navigate({ surface: o.surface }); };
    // Only announce a HIT (ok:true). A miss is decided by the orb's timeout, so another handler
    // (e.g. the Performance hub focusing a site domain) can still claim the command first.
    const hit = (intent: string, label?: string) => window.dispatchEvent(new CustomEvent('beast:voice-command-result', { detail: { intent, ok: true, label } }));
    const on = (e: Event) => {
      const { intent, arg } = (e as CustomEvent).detail || {};
      if (intent === 'back') { back(); hit('back'); }
      else if (intent === 'home') { navigate({ deck: 'menu' }); hit('home'); }
      else if (intent === 'open') { const c = resolve(arg || ''); if (c) { openCard(c); hit('open', c.title); } }
    };
    window.addEventListener('beast:voice-command', on);
    return () => window.removeEventListener('beast:voice-command', on);
  }, [navigate, back]);

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Backspace') {
        const t = e.target as HTMLElement;
        if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') return;
        if (!hist.current.length) return; // at root: let Esc fall through (Chrome exits to classic)
        e.preventDefault();
        back();
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [back]);

  const isDeck = 'deck' in view;

  // orb spec position: broadcast hangar vs room so CoreOrb can translate (v4 t4OrbStyle)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('uif:space', { detail: isDeck ? 'hangar' : 'room' }));
  }, [isDeck]);

  return (
    <div className="absolute inset-0" style={{ perspective: 1400 }}>
      {/* BACK pill — spec backStyle VERBATIM (left 36 top 78) */}
      <button onMouseUp={back} data-lock="BACK"
        style={{ position: 'absolute', left: 36, top: 104, /* small — below the shrunken chrome (Boss 2026-07-10) */ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 11px', borderRadius: 999, border: '1px solid rgba(125,232,255,.25)', color: '#9ef0ff', fontFamily: 'var(--uif-font-mono)', fontSize: 13, letterSpacing: '.22em', background: 'transparent', transition: 'opacity .3s,background .25s,border-color .25s', opacity: hist.current.length ? 1 : 0, pointerEvents: hist.current.length ? 'auto' : 'none', zIndex: 56 }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,240,255,.07)'; e.currentTarget.style.borderColor = 'rgba(158,240,255,.55)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(125,232,255,.25)'; }}>
        ‹ BACK
      </button>
      {/* dive flash — spec t4FlashStyle VERBATIM */}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(closest-side at 50% 55%, rgba(190,245,255,.32), rgba(0,240,255,.12) 42%, transparent 78%)', opacity: diving !== null ? 1 : 0, transition: diving !== null ? 'opacity .3s ease-in .58s' : 'opacity .45s ease-out', pointerEvents: 'none', zIndex: 75 }} />

      <div style={{
        position: 'absolute', inset: 0, transformOrigin: '50% 45%',
        transform: fly?.phase === 'out' ? `scale(${fly.dir === 'fwd' ? 2.5 : .5})`
          : fly?.phase === 'pre' ? `scale(${fly.dir === 'fwd' ? .5 : 2.5})`
          : fly?.phase === 'in' ? 'scale(1)' : 'none',
        opacity: fly?.phase === 'out' ? 0 : fly?.phase === 'pre' ? 0 : 1,
        transition: fly?.phase === 'out' ? 'transform .42s cubic-bezier(.6,0,.85,.4),opacity .42s cubic-bezier(.6,0,.85,.4)'
          : fly?.phase === 'in' ? 'transform .6s cubic-bezier(.2,1.1,.3,1),opacity .3s'
          : 'none',
      }}>
        {isDeck
          ? <Deck key={(view as { deck: string }).deck} deck={(view as { deck: string }).deck}
              diving={diving} setDiving={setDiving} navigate={(v) => navigate(v, { dive: true })} />
          : <SurfaceHost id={(view as { surface: string }).surface} navigate={(v) => navigate(v)} back={back} />}
      </div>
    </div>
  );
}

function Deck({ deck, diving, setDiving, navigate }: {
  deck: string; diving: number | null;
  setDiving: (i: number | null) => void;
  navigate: (v: View) => void;
}) {
  const cards = DECKS[deck] || [];
  const stats = useDeckStats(deck);
  // arriving law (spec diveItem): cards materialize from scale .04, released after 80ms
  const [arriving, setArriving] = useState(true);
  useEffect(() => { const t = setTimeout(() => setArriving(false), 80); return () => clearTimeout(t); }, []);

  const pick = useCallback((i: number) => {
    const card = cards[i];
    if (!card) return;
    setDiving(i);
    setTimeout(() => {
      const o = card.opens as any;
      navigate(o.deck ? { deck: o.deck } : { surface: o.surface });
      setDiving(null);
    }, 640);
  }, [cards, navigate, setDiving]);

  // spec tap law: focused card dives; side card glides to center, then
  // auto-dives after 700ms if still focused (v4 autoDive)
  const autoDive = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusRef = useRef(0);
  const car = useCarousel(cards.length, (i) => {
    if (i === focusRef.current) { pick(i); return; }
    car.goTo(i);
    if (autoDive.current) clearTimeout(autoDive.current);
    autoDive.current = setTimeout(() => { if (focusRef.current === i) pick(i); }, 700);
  });
  focusRef.current = car.focus;
  useEffect(() => () => { if (autoDive.current) clearTimeout(autoDive.current); }, []);

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      // typing in a chat input must NEVER drive the carousel (Boss 2026-07-10:
      // Return sent a message AND dove into the focused holo panel)
      const t = e.target as HTMLElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'ArrowLeft') car.step(-1);
      if (e.key === 'ArrowRight') car.step(1);
      if (e.key === 'Enter') pick(car.focus);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car.step, car.focus, pick]);

  // VOICE SCROLL (Boss 2026-07-11 'scroll horizontal and vertical'): left/right step the
  // carousel, up/down scroll the page. Announce the hit so the orb confirms the move landed.
  useEffect(() => {
    const on = (e: Event) => {
      const { intent, arg } = (e as CustomEvent).detail || {};
      // OPEN-THE-FOCUSED-CARD (Boss 2026-07-12 'the one I'm looking at — if I don't name it, it's
      // always the one pinched to focus'): when Boss says 'open it / this one / this / that / here'
      // with no concrete target, dive the card currently in focus. The name-resolver at the Shell
      // level ignores these deictic words (they score below its match bar), so there's no conflict.
      if (intent === 'open') {
        const a = String(arg || '').toLowerCase().replace(/[^a-z ]/g, ' ').replace(/\s+/g, ' ').trim();
        const deictic = a === '' || /^(it|this|that|these|those|here|current|currently|focus|focused|selected|the one|this one|that one|the current one|the focused one|the selected one)$/.test(a);
        if (deictic) {
          pick(car.focus);
          window.dispatchEvent(new CustomEvent('beast:voice-command-result', { detail: { intent: 'open', ok: true, label: 'focused' } }));
        }
        return;
      }
      if (intent !== 'scroll') return;
      if (arg === 'left') car.step(-1);
      else if (arg === 'right') car.step(1);
      else if (arg === 'up') window.scrollBy({ top: -Math.round(window.innerHeight * 0.8), behavior: 'smooth' });
      else if (arg === 'down') window.scrollBy({ top: Math.round(window.innerHeight * 0.8), behavior: 'smooth' });
      window.dispatchEvent(new CustomEvent('beast:voice-command-result', { detail: { intent: 'scroll', ok: true, label: arg } }));
    };
    window.addEventListener('beast:voice-command', on);
    return () => window.removeEventListener('beast:voice-command', on);
  }, [car.step, car.focus, pick]);

  const edgeStyle = (side: 'left' | 'right'): React.CSSProperties => ({
    position: 'absolute', top: 470, [side]: 18, width: 64, height: 120, display: 'grid', placeItems: 'center',
    zIndex: 45, borderRadius: 14, opacity: .85, background: 'transparent', border: 'none', transition: 'opacity .3s',
  });

  // non-passive wheel binding — preventDefault() must be able to kill the
  // browser's two-finger history swipe (React's onWheel prop is passive)
  const stageEl = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = stageEl.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => car.onWheel(e);
    el.addEventListener('wheel', wheel, { passive: false });
    return () => el.removeEventListener('wheel', wheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car.onWheel]);

  return (
    <div ref={stageEl} className="absolute inset-0"
      onPointerDown={car.onPointerDown} onPointerMove={car.onPointerMove}
      onPointerUp={(e) => car.onPointerUp(e)}>
      {/* deck title */}
      <div className="absolute top-[72px] left-1/2 -translate-x-1/2" style={{ ...mono(18, '.42em'), color: 'var(--uif-t5)' }}>
        {DECK_TITLE[deck] || deck}
      </div>
      {/* cards — v4 hangar VERBATIM: absolute 1920×1080 stage coords (left 810+680·sinθ, top 378+142·cosθ) */}
      {cards.map((c, i) => {
        const off = car.offsetOf(i);
        const style = cardStyle(off, { scrubbing: car.scrubbing, diving: diving !== null, arriving });
        return (
          <div key={c.id} style={style}>
            <HoloCard card={c} stat={stats[c.id]} focused={Math.abs(off) < 0.4 && diving === null} far={Math.abs(off) >= 1.6}
              onPick={() => car.tap(i)} />
          </div>
        );
      })}
      {/* edge chevrons — spec edge() + 26×46 svg VERBATIM */}
      <button onMouseUp={() => car.step(-1)} data-lock="PREV" style={edgeStyle('left')}>
        <svg width="26" height="46" viewBox="0 0 26 46" fill="none" stroke="rgba(158,240,255,.7)" strokeWidth="2"><path d="M20 6 L7 23 L20 40" /></svg>
      </button>
      <button onMouseUp={() => car.step(1)} data-lock="NEXT" style={edgeStyle('right')}>
        <svg width="26" height="46" viewBox="0 0 26 46" fill="none" stroke="rgba(158,240,255,.7)" strokeWidth="2"><path d="M6 6 L19 23 L6 40" /></svg>
      </button>
    </div>
  );
}

/* v4 spec hangar card — panel styles VERBATIM from Control Room v4 t4Carousel:
   300×190 panel (rotateX 5°, gradient glass, scanlines, brand hairline), sigil
   26×26 r8 + name + LED, two 20px KPIs w/ delta, mono foot; light cone
   (clip-path polygon(8% 0,92% 0,54% 100%,46% 100%)), floor pad ellipse, glow dot,
   PINCH TO OPEN hint at 344. */
function HoloCard({ card, stat, focused, far, onPick }: { card: Card; stat?: CardStat; focused: boolean; far?: boolean; onPick: () => void }) {
  const color = card.color || '#7de8ff';
  return (
    <div onMouseUp={onPick} role="button" data-lock={card.title.toUpperCase()} className="relative w-[340px] h-[406px]">
      <div style={{
        position: 'absolute', left: 0, top: 0, width: 340, height: 260, boxSizing: 'border-box',
        borderRadius: 12, padding: 14,
        background: 'linear-gradient(170deg,rgba(40,140,180,.08),rgba(8,18,28,.4) 55%)',
        border: `1px solid rgba(158,240,255,${focused ? .55 : .26})`,
        boxShadow: focused ? '0 0 70px -8px rgba(0,240,255,.4)' : '0 0 40px -12px rgba(0,240,255,.18)',
        transform: 'perspective(900px) rotateX(5deg)', transformOrigin: '50% 100%',
        overflow: 'hidden', transition: 'border-color .4s,box-shadow .4s',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(180deg,rgba(140,235,255,.05) 0 1px,transparent 1px 3px)', mixBlendMode: 'screen', opacity: .5, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 0, left: '12%', right: '12%', height: 1, background: `linear-gradient(90deg,transparent,${color},transparent)` }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          {/* minWidth+padding — two-char sigils overflowed the fixed 26px box into the title (Boss 2026-07-10) */}
          <span style={{ display: 'grid', placeItems: 'center', minWidth: 30, height: 28, padding: '0 6px', flexShrink: 0, borderRadius: 8, border: `1px solid color-mix(in srgb, ${color} 45%, transparent)`, background: `color-mix(in srgb, ${color} 8%, transparent)`, fontFamily: 'var(--uif-font-display)', fontSize: 16, color, textShadow: `0 0 8px color-mix(in srgb, ${color} 60%, transparent)` }}>{card.sigil || card.title[0]}</span>
          <span style={{ flex: 1, fontWeight: 500, fontSize: 24, letterSpacing: '.04em', color: '#e9f9ff', textShadow: '0 0 10px rgba(0,240,255,.2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{card.title}</span>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#5df2b4', animation: 'bccPulse 2.2s infinite' }} />
        </div>
        {stat ? (
          <div style={{ display: 'flex', gap: 16, marginTop: 11, opacity: far ? 0 : 1, transition: 'opacity .4s' }}>
            <div style={{ flex: 1 }}>
              <div style={{ ...mono(14, '.18em'), color: '#49677a', marginBottom: 2 }}>{stat.k1l}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                <span style={{ fontWeight: 200, fontSize: 34, color: '#aef0ff', textShadow: '0 0 14px rgba(0,240,255,.35)' }}>{stat.k1v}</span>
                {stat.k1d && <span style={{ ...mono(16), color: stat.k1up ? '#5df2b4' : '#ff7d8a' }}>{stat.k1d}</span>}
              </div>
            </div>
            {stat.k2l && (
              <div style={{ flex: 1 }}>
                <div style={{ ...mono(14, '.18em'), color: '#49677a', marginBottom: 2 }}>{stat.k2l}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                  <span style={{ fontWeight: 200, fontSize: 34, color: '#d7f2ff', textShadow: '0 0 8px rgba(0,240,255,.18)' }}>{stat.k2v}</span>
                  {stat.k2d && <span style={{ ...mono(16), color: stat.k2up ? '#5df2b4' : '#ff7d8a' }}>{stat.k2d}</span>}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginTop: 14, fontWeight: 300, fontSize: 20, lineHeight: 1.5, color: '#9fb8c6', opacity: far ? 0 : 1, transition: 'opacity .4s' }}>{card.sub}</div>
        )}
        {/* sparkline — REAL series only (stat.spark); no data → no line. Fake decorative path removed (Boss 2026-07-10: 'those lines represent nothing') */}
        {stat?.spark && stat.spark.length > 1 && (() => {
          const pts = stat.spark!; const min = Math.min(...pts), max = Math.max(...pts), sp = max - min || 1;
          const d = pts.map((v, i) => `${i ? 'L' : 'M'}${(i / (pts.length - 1)) * 260},${34 - ((v - min) / sp) * 28}`).join(' ');
          return (
            <svg width="100%" height="38" viewBox="0 0 260 40" preserveAspectRatio="none" style={{ display: 'block', marginTop: 10 }}>
              <path d={d} fill="none" stroke="#9ef0ff" strokeWidth="1.3" vectorEffect="non-scaling-stroke" style={{ filter: 'drop-shadow(0 0 5px rgba(0,240,255,.7))' }} />
            </svg>
          );
        })()}
        <div style={{ position: 'absolute', left: 14, right: 14, bottom: 12, ...mono(15, '.16em'), color: '#49677a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', opacity: far ? 0 : 1, transition: 'opacity .4s' }}>{stat?.foot || card.sub || ''}</div>
      </div>
      {/* light cone + floor pad + glow dot — verbatim */}
      <div style={{ position: 'absolute', left: 95, top: 260, width: 150, height: 110, background: 'linear-gradient(0deg,rgba(0,240,255,.14),rgba(0,240,255,.02) 80%)', clipPath: 'polygon(8% 0, 92% 0, 54% 100%, 46% 100%)', animation: 'bccFlick 5s infinite', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', left: 100, top: 366, width: 140, height: 30, borderRadius: '50%', border: '1px solid rgba(125,232,255,.35)', background: 'radial-gradient(closest-side,rgba(0,240,255,.16),transparent 72%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', left: 167, top: 378, width: 6, height: 6, borderRadius: '50%', background: '#9ef0ff', boxShadow: '0 0 12px rgba(0,240,255,.9)', pointerEvents: 'none' }} />
    </div>
  );
}
